import { create } from 'zustand';

export type GallerySlide =
  | {
      /**
       * Accessible description of the image
       */
      alt?: string;
      /**
       * Whether to show a download button; pass a string to use as the filename
       */
      download?: boolean | string;
      /**
       * Logical display height in CSS pixels (half of xl so 2× zoom reaches xl natural pixels)
       */
      height?: number;
      /**
       * Image URL
       */
      src: string;
      /**
       * Alternative sources for different viewport sizes; browser picks the best fit
       */
      srcSet?: { height: number; src: string; width: number }[];
      /**
       * Discriminant — identifies this variant as an image slide
       */
      type: 'image';
      /**
       * Logical display width in CSS pixels (half of xl so 2× zoom reaches xl natural pixels)
       */
      width?: number;
    }
  | {
      /**
       * YouTube video embed URL
       */
      embeddedUrl: string;
      /**
       * Poster image shown before the video loads
       */
      poster?: string;
      /**
       * Discriminant — identifies this variant as a YouTube slide
       */
      type: 'youtube';
    };

interface PhotoGalleryState {
  /**
   * Close the gallery (preserves slides for unmount transitions)
   */
  closeGallery: () => void;
  /**
   * Current slide index
   */
  index: number;
  /**
   * Whether the lightbox is currently open
   */
  isOpen: boolean;
  /**
   * Open the gallery with a slide list and starting index
   */
  openGallery: (slides: GallerySlide[], index?: number) => void;
  /**
   * Update the current slide index (driven by YARL view callback)
   */
  setIndex: (index: number) => void;
  /**
   * Slide list driving the lightbox
   */
  slides: GallerySlide[];
}

export const usePhotoGalleryStore = create<PhotoGalleryState>(set => ({
  closeGallery: () => set({ isOpen: false }),
  index: 0,
  isOpen: false,
  openGallery: (slides, index = 0) => set({ index, isOpen: true, slides }),
  setIndex: index => set({ index }),
  slides: [],
}));
